import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faImage,
    faPlus,
    faSave,
    faDownload,
    faCopy,
    faTrash,
    faEdit,
    faEye,
    faTextHeight,
    faPhotoVideo,
    faQrcode,
    faShapes,
    faIcons,
    faPalette,
    faCheckCircle,
    faInfoCircle,
    faArrowRight,
    faArrowLeft,
    faRulerCombined
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function MarketingInfografias() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <FontAwesomeIcon icon={faImage} className="text-purple-600 text-2xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Generar Infografías
                    </h1>
                    <p className="text-xl text-gray-600">
                        Aprende a crear infografías profesionales y personalizadas para tu marca usando 
                        nuestro editor visual intuitivo con templates predefinidos y herramientas avanzadas.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        ¿Qué es el Generador de Infografías?
                    </h2>
                    <p className="text-gray-600 mb-4">
                        El generador de infografías es una herramienta visual que te permite crear diseños 
                        profesionales para promocionar tu negocio, compartir información o crear contenido 
                        para redes sociales. Con esta herramienta puedes:
                    </p>
                    <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Crear infografías desde templates predefinidos o desde cero</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Añadir texto, imágenes, códigos QR, formas e iconos</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Personalizar colores, fuentes y estilos</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Exportar en diferentes formatos (PDF, PNG)</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Guardar y reutilizar tus diseños</span>
                        </li>
                    </ul>
                </div>

                {/* Acceso */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faImage} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Acceder al Generador
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cómo Acceder
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde el menú principal, accede a <strong>"Marketing" → "Generar Infografías"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>O desde la gestión de marcas, selecciona una marca y haz clic en <strong>"Infografías"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Se abrirá el editor de infografías</span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Crear Nueva Infografía */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPlus} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Crear una Nueva Infografía
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 1: Seleccionar Template
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Al crear una nueva infografía, primero debes elegir un template:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Feedback</h4>
                                <p className="text-sm text-gray-600">
                                    Template para solicitar opiniones y reseñas de clientes
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Promocional</h4>
                                <p className="text-sm text-gray-600">
                                    Template para promociones y ofertas especiales
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Reviews</h4>
                                <p className="text-sm text-gray-600">
                                    Template para mostrar reseñas y testimonios
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Personalizado</h4>
                                <p className="text-sm text-gray-600">
                                    Crea desde cero con dimensiones personalizadas
                                </p>
                            </div>
                        </div>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Consejo:</strong> Si eliges "Personalizado", podrás seleccionar las dimensiones 
                                exactas que necesitas (ancho, alto, unidad, DPI).
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 2: Configurar Dimensiones (Solo Personalizado)
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Si seleccionaste "Personalizado", configura las dimensiones:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faRulerCombined} className="text-purple-500 mr-2 mt-1" />
                                <span><strong>Ancho y Alto:</strong> Dimensiones en píxeles (ej: 2480x3508 para A4)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Unidad:</strong> Generalmente píxeles (px)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>DPI:</strong> Resolución (300 DPI recomendado para impresión)</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Editor Visual */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faEdit} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Usar el Editor Visual
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Interfaz del Editor
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El editor está dividido en varias secciones:
                        </p>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Canvas (Área de Trabajo)</h4>
                                <p className="text-sm text-gray-600">
                                    El área central donde puedes ver y editar tu infografía. Puedes hacer zoom 
                                    (+, -) o ajustar al tamaño de la ventana (Fit).
                                </p>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Barra de Herramientas</h4>
                                <p className="text-sm text-gray-600">
                                    Contiene botones para añadir diferentes tipos de elementos a tu infografía.
                                </p>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Panel de Propiedades</h4>
                                <p className="text-sm text-gray-600">
                                    Muestra y permite editar las propiedades del elemento seleccionado (texto, 
                                    colores, tamaño, posición, etc.).
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Añadir Elementos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Desde la barra de herramientas puedes añadir diferentes tipos de elementos:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faTextHeight} className="text-blue-500 mr-2" />
                                    Texto
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Añade cajas de texto con contenido personalizable. Puedes editar el texto, 
                                    fuente, tamaño, color y alineación.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faPhotoVideo} className="text-green-500 mr-2" />
                                    Imagen
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Sube imágenes desde tu ordenador (JPEG, PNG, WebP, GIF). Máximo 10MB por imagen.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faQrcode} className="text-purple-500 mr-2" />
                                    Código QR
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Genera códigos QR con URLs personalizadas. Útil para enlaces a tu web, 
                                    redes sociales o promociones.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faShapes} className="text-orange-500 mr-2" />
                                    Formas
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Añade rectángulos y círculos con colores y tamaños personalizables. 
                                    Útiles para fondos y decoración.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faIcons} className="text-red-500 mr-2" />
                                    Iconos
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Selecciona iconos de una biblioteca para añadir a tu diseño.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Editar Elementos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Para editar un elemento:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Haz clic en el elemento que quieres editar en el canvas</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>El panel de propiedades se actualizará automáticamente con las opciones del elemento</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Modifica las propiedades (texto, color, tamaño, posición, etc.)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Los cambios se aplican en tiempo real en el canvas</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Eliminar Elementos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Para eliminar un elemento:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faTrash} className="text-red-500 mr-2 mt-1" />
                                <span>Selecciona el elemento y presiona <strong>Delete</strong> o <strong>Backspace</strong> en tu teclado</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faTrash} className="text-red-500 mr-2 mt-1" />
                                <span>O usa el botón de eliminar en el panel de propiedades</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Propiedades de Elementos */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPalette} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Propiedades de Elementos
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Propiedades de Texto
                        </h3>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Contenido:</strong> El texto que se mostrará</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Fuente:</strong> Tipo de letra (Arial, Times New Roman, etc.)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tamaño:</strong> Tamaño de la fuente en píxeles</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Color:</strong> Color del texto</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Alineación:</strong> Izquierda, centro, derecha, justificado</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Estilos:</strong> Negrita, cursiva, subrayado</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Propiedades de Imagen
                        </h3>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Subir imagen:</strong> Selecciona un archivo desde tu ordenador</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Formatos soportados:</strong> JPEG, PNG, WebP, GIF</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tamaño máximo:</strong> 10MB por imagen</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Opacidad:</strong> Controla la transparencia de la imagen</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Propiedades de Código QR
                        </h3>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>URL:</strong> La dirección web a la que apuntará el código QR</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Color:</strong> Color del código QR</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tamaño:</strong> Tamaño del código QR</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Propiedades de Formas
                        </h3>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tipo:</strong> Rectángulo o círculo</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Color de relleno:</strong> Color interior de la forma</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Color de borde:</strong> Color del contorno</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tamaño:</strong> Ancho y alto de la forma</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Fondo */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPalette} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Personalizar el Fondo
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Tipos de Fondo
                        </h3>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Color Sólido</h4>
                                <p className="text-sm text-gray-600">
                                    Selecciona un color sólido para el fondo de tu infografía. Puedes usar el selector 
                                    de color para elegir cualquier color.
                                </p>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Gradiente</h4>
                                <p className="text-sm text-gray-600">
                                    Crea un fondo con gradiente de colores. Puedes configurar múltiples colores 
                                    y la dirección del gradiente.
                                </p>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Imagen</h4>
                                <p className="text-sm text-gray-600">
                                    Usa una imagen como fondo. Puedes subir una imagen desde tu ordenador o usar 
                                    una imagen existente.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Guardar y Exportar */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faSave} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Guardar y Exportar
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Autoguardado
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El sistema guarda automáticamente tu trabajo cada 30 segundos. No necesitas preocuparte 
                            por perder tus cambios.
                        </p>
                        <div className="bg-green-50 border-l-4 border-green-500 p-4">
                            <p className="text-sm text-green-800">
                                <strong>✅ Autoguardado Activo:</strong> Verás un indicador cuando se guarde automáticamente. 
                                Tu trabajo está siempre protegido.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Guardar Manualmente
                        </h3>
                        <p className="text-gray-600 mb-4">
                            También puedes guardar manualmente en cualquier momento:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Haz clic en el botón <strong>"Guardar"</strong> (icono de disco)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Si es la primera vez, ingresa un nombre para la infografía</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>La infografía se guardará y aparecerá en tu lista de infografías</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Vista Previa
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Antes de exportar, puedes ver una vista previa completa:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Haz clic en el botón <strong>"Vista Previa"</strong> o <strong>"Preview"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Se abrirá un modal mostrando cómo se verá la infografía final</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Desde aquí puedes exportar directamente</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Exportar Infografía
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes exportar tu infografía en diferentes formatos:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faDownload} className="text-blue-500 mr-2" />
                                    PDF
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Ideal para imprimir o compartir documentos profesionales. Mantiene alta calidad.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faDownload} className="text-green-500 mr-2" />
                                    PNG
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Formato de imagen de alta calidad. Perfecto para redes sociales y web.
                                </p>
                            </div>
                        </div>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Consejo:</strong> Usa PDF para documentos que quieras imprimir y PNG para 
                                compartir en redes sociales o incluir en tu sitio web.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Gestionar Infografías */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faImage} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Gestionar Infografías
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Ver Infografías Guardadas
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes ver todas tus infografías guardadas desde el selector de templates. 
                            Las infografías existentes aparecerán en la lista y puedes:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faEdit} className="text-blue-500 mr-2 mt-1" />
                                <span><strong>Editar:</strong> Abrir una infografía existente para modificarla</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCopy} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Duplicar:</strong> Crear una copia de una infografía existente</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faTrash} className="text-red-500 mr-2 mt-1" />
                                <span><strong>Eliminar:</strong> Eliminar una infografía que ya no necesites</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Consejos Finales */}
                <div className="bg-purple-50 rounded-lg p-8 mb-8">
                    <h3 className="text-lg font-semibold text-purple-800 mb-4">
                        💡 Consejos para Crear Infografías Exitosas
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Usa templates como punto de partida:</strong> Los templates te dan una base 
                            sólida que puedes personalizar según tus necesidades.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Mantén el diseño simple:</strong> No sobrecargues la infografía con demasiados 
                            elementos. Menos es más.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Usa colores de tu marca:</strong> Mantén la consistencia visual usando los 
                            colores corporativos de tu marca.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Textos legibles:</strong> Asegúrate de que los textos sean lo suficientemente 
                            grandes y contrasten bien con el fondo.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Guarda frecuentemente:</strong> Aunque el autoguardado está activo, guarda 
                            manualmente cuando completes secciones importantes.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Prueba antes de exportar:</strong> Usa la vista previa para asegurarte de que 
                            todo se ve correctamente antes de exportar.</span>
                        </li>
                    </ul>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-6 border-t">
                    <Link
                        to="/documentation/marketing/email-marketing"
                        className="text-gray-600 hover:text-gray-800 font-medium inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Email Marketing
                    </Link>
                    <Link
                        to="/documentation/integraciones"
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center"
                    >
                        Siguiente: Integraciones
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default MarketingInfografias;

